import { useCallback } from 'react';
import { UseQueryExecute } from 'urql';

import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { SyncOutline } from '@lyearn/icons';
import { Loader, Stack, Typography, useSnackbar } from '@lyearn/ui';
import { CourseLanguageInfoFragment } from '@/modules/corporate/modules/course/graphql/fragments/generated/CourseLanguageInfo';
import { useSyncLocalizedContentMutation } from '@/modules/corporate/modules/course/graphql/mutation/generated/syncLocalizedContent';
import { QuizLanguageInfoFragment } from '@/modules/corporate/modules/quiz/views/QuizEditor/graphql/fragments/generated/QuizLanguageInfo';
import { CourseContentType, LanguageEnum } from '@/types/schema';

type Props = {
  language: LanguageEnum;
  content: CourseLanguageInfoFragment | QuizLanguageInfoFragment;
  lnaguageConfig?: { contentId: string; languageType: LanguageEnum };
  refetchLocalizedContent?: UseQueryExecute;
};
export const SyncNowButton = ({
  language,
  content,
  lnaguageConfig,
  refetchLocalizedContent,
}: Props) => {
  const { showSnackbar, showErrorSnackbar } = useSnackbar();

  const [{ fetching: isSyncing }, syncLocalizedContent] = useSyncLocalizedContentMutation();
  const { t } = useTranslation();
  const onSync = useCallback(
    async (localizedEntityId: string) => {
      const { isLocalizedContent, sourceContent, id } = content;
      await syncLocalizedContent({
        data: {
          entityId: isLocalizedContent ? (sourceContent as CourseContentType)?.id || '' : id,
          localizedEntityId,
        },
      }).then((res) => {
        if (res.data?.syncLocalizedContent.success) {
          if (!isLocalizedContent) refetchLocalizedContent?.();
          showSnackbar({
            message: t('Course:SyncLocalizedContentSuccess'),
          });
        } else {
          showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
        }
      });
    },
    [content, syncLocalizedContent, showSnackbar, t, showErrorSnackbar, refetchLocalizedContent],
  );

  return (
    <Stack
      alignItems="center"
      className="rounded-l-[9999px] rounded-r-[9999px] cursor-pointer hover:bg-interactive-secondary-hover px-10 py-4 bg-bg-secondary"
      gapX={2}
      onClick={(event) => {
        if (isSyncing) {
          return;
        }
        stopEventPropagation(event);
        onSync(lnaguageConfig?.contentId as string);
      }}>
      {isSyncing ? (
        <Loader className="mx-[7.45px]" size={18} />
      ) : (
        <>
          <SyncOutline className="h-10 w-10" />
          <Typography variant="label-03">{t('Course:SyncNow')}</Typography>
        </>
      )}
    </Stack>
  );
};
